/**
 * ConfigPage — super_admin system configuration management.
 *
 * Provides 6 tabs for managing all admin-controlled configuration entities:
 *   1. Planes        — subscription plans (name, slug, price, billing period)
 *   2. Add-ons       — optional add-on products
 *   3. Paises        — countries reference table
 *   4. Regiones      — regions (sub-divisions of countries)
 *   5. Webhooks      — outbound webhook endpoints
 *   6. API Clients   — machine-to-machine API credentials
 *
 * Security: Route is protected at the router level with AuthGuard.
 * Role check at component level returns a 403-style message for non-super_admins.
 *
 * API key creation UX: raw key shown exactly once in a modal with copy button.
 * After dismissal it is unrecoverable — user is clearly warned.
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  usePlans,
  useCreatePlan,
  useAddons,
  useCreateAddon,
  useConfigCountries,
  useCreateCountry,
  useConfigRegions,
  useCreateRegion,
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useApiClients,
  useCreateApiClient,
  useToggleApiClient,
  type CreatePlanInput,
  type CreateAddonInput,
  type CreateCountryInput,
  type CreateRegionInput,
  type CreateWebhookInput,
  type CreateApiClientInput,
  type CreateApiClientResult,
} from '@/hooks/useConfig'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type Tab = 'planes' | 'addons' | 'paises' | 'regiones' | 'webhooks' | 'api-clients'

const TABS: { id: Tab; label: string }[] = [
  { id: 'planes', label: 'Planes' },
  { id: 'addons', label: 'Add-ons' },
  { id: 'paises', label: 'Paises' },
  { id: 'regiones', label: 'Regiones' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'api-clients', label: 'API Clients' },
]

// ── Shared UI components ──────────────────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  )
}

function SectionHeader({ title, onAdd }: { title: string; onAdd: () => void }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      <button
        onClick={onAdd}
        className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Crear
      </button>
    </div>
  )
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null
  return <p className="mt-1 text-sm text-red-600">{message}</p>
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Cerrar"
          >
            x
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

function Badge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

// ── Planes tab ─────────────────────────────────────────────────────────────────

function PlanesTab() {
  const { data: plans, isLoading, isError } = usePlans()
  const createPlan = useCreatePlan()
  const [showModal, setShowModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreatePlanInput>()

  const onSubmit = async (data: CreatePlanInput) => {
    setCreateError(null)
    try {
      await createPlan.mutateAsync({
        ...data,
        priceCents: Number(data.priceCents),
        sortOrder: data.sortOrder ? Number(data.sortOrder) : undefined,
      })
      setShowModal(false)
      reset()
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el plan')
    }
  }

  return (
    <div>
      <SectionHeader title="Planes de Suscripcion" onAdd={() => { setShowModal(true); reset(); setCreateError(null) }} />

      {isLoading && <p className="py-8 text-center text-gray-500">Cargando planes...</p>}
      {isError && <p className="py-8 text-center text-red-500">Error al cargar los planes</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nombre', 'Slug', 'Precio', 'Periodo', 'Estado'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(plans ?? []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No hay planes configurados
                  </td>
                </tr>
              ) : (
                (plans ?? []).map((plan) => (
                  <tr key={plan.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{plan.slug}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCents(plan.price_cents)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{plan.billing_period}</td>
                    <td className="px-4 py-3"><Badge active={plan.active} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Nuevo Plan" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre *</label>
              <input
                {...register('name', { required: 'Requerido' })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.name && <FormError message={errors.name.message ?? null} />}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Slug * (solo minusculas, numeros, guiones)</label>
              <input
                {...register('slug', { required: 'Requerido', pattern: { value: /^[a-z0-9-]+$/, message: 'Solo minusculas, numeros y guiones' } })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.slug && <FormError message={errors.slug.message ?? null} />}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Precio (centavos) *</label>
              <input
                type="number"
                min={0}
                {...register('priceCents', { required: 'Requerido', valueAsNumber: true, min: { value: 0, message: 'Debe ser >= 0' } })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.priceCents && <FormError message={errors.priceCents.message ?? null} />}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Periodo de facturacion</label>
              <select
                {...register('billingPeriod')}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="monthly">Mensual</option>
                <option value="annual">Anual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Descripcion</label>
              <textarea
                {...register('description')}
                rows={2}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <FormError message={createError} />

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {isSubmitting ? 'Creando...' : 'Crear Plan'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

// ── Add-ons tab ────────────────────────────────────────────────────────────────

function AddonsTab() {
  const { data: addons, isLoading, isError } = useAddons()
  const createAddon = useCreateAddon()
  const [showModal, setShowModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateAddonInput>()

  const onSubmit = async (data: CreateAddonInput) => {
    setCreateError(null)
    try {
      await createAddon.mutateAsync({ ...data, priceCents: Number(data.priceCents) })
      setShowModal(false)
      reset()
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el add-on')
    }
  }

  return (
    <div>
      <SectionHeader title="Add-ons" onAdd={() => { setShowModal(true); reset(); setCreateError(null) }} />

      {isLoading && <p className="py-8 text-center text-gray-500">Cargando add-ons...</p>}
      {isError && <p className="py-8 text-center text-red-500">Error al cargar los add-ons</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nombre', 'Precio', 'Descripcion', 'Estado'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(addons ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">No hay add-ons configurados</td>
                </tr>
              ) : (
                (addons ?? []).map((addon) => (
                  <tr key={addon.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{addon.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCents(addon.price_cents)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{addon.description ?? '-'}</td>
                    <td className="px-4 py-3"><Badge active={addon.active} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Nuevo Add-on" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre *</label>
              <input
                {...register('name', { required: 'Requerido' })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.name && <FormError message={errors.name.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Precio (centavos) *</label>
              <input
                type="number"
                min={0}
                {...register('priceCents', { required: 'Requerido', valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.priceCents && <FormError message={errors.priceCents.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Descripcion</label>
              <input
                {...register('description')}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <FormError message={createError} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={isSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Creando...' : 'Crear Add-on'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

// ── Paises tab ─────────────────────────────────────────────────────────────────

function PaisesTab() {
  const { data: countries, isLoading, isError } = useConfigCountries()
  const createCountry = useCreateCountry()
  const [showModal, setShowModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateCountryInput>()

  const onSubmit = async (data: CreateCountryInput) => {
    setCreateError(null)
    try {
      await createCountry.mutateAsync(data)
      setShowModal(false)
      reset()
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el pais')
    }
  }

  return (
    <div>
      <SectionHeader title="Paises" onAdd={() => { setShowModal(true); reset(); setCreateError(null) }} />

      {isLoading && <p className="py-8 text-center text-gray-500">Cargando paises...</p>}
      {isError && <p className="py-8 text-center text-red-500">Error al cargar los paises</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nombre', 'Codigo', 'Estado'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(countries ?? []).length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500">No hay paises configurados</td></tr>
              ) : (
                (countries ?? []).map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-600">{c.code}</td>
                    <td className="px-4 py-3"><Badge active={c.active} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Nuevo Pais" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre *</label>
              <input {...register('name', { required: 'Requerido' })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              {errors.name && <FormError message={errors.name.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Codigo ISO 3166-1 alpha-2 (2 letras) *</label>
              <input
                {...register('code', { required: 'Requerido', minLength: { value: 2, message: 'Exactamente 2 caracteres' }, maxLength: { value: 2, message: 'Exactamente 2 caracteres' } })}
                maxLength={2}
                placeholder="CO"
                className="mt-1 block w-24 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono uppercase focus:border-blue-500 focus:outline-none"
              />
              {errors.code && <FormError message={errors.code.message ?? null} />}
            </div>
            <FormError message={createError} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={isSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Creando...' : 'Crear Pais'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

// ── Regiones tab ───────────────────────────────────────────────────────────────

function RegionesTab() {
  const { data: regions, isLoading, isError } = useConfigRegions()
  const { data: countries } = useConfigCountries()
  const createRegion = useCreateRegion()
  const [showModal, setShowModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateRegionInput>()

  const onSubmit = async (data: CreateRegionInput) => {
    setCreateError(null)
    try {
      await createRegion.mutateAsync(data)
      setShowModal(false)
      reset()
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear la region')
    }
  }

  return (
    <div>
      <SectionHeader title="Regiones" onAdd={() => { setShowModal(true); reset(); setCreateError(null) }} />

      {isLoading && <p className="py-8 text-center text-gray-500">Cargando regiones...</p>}
      {isError && <p className="py-8 text-center text-red-500">Error al cargar las regiones</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nombre', 'Pais', 'Estado'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(regions ?? []).length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500">No hay regiones configuradas</td></tr>
              ) : (
                (regions ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{r.country_name ?? '-'}</td>
                    <td className="px-4 py-3"><Badge active={r.active} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Nueva Region" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre *</label>
              <input {...register('name', { required: 'Requerido' })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              {errors.name && <FormError message={errors.name.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pais *</label>
              <select
                {...register('countryId', { required: 'Requerido' })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="">Seleccionar pais...</option>
                {(countries ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
              {errors.countryId && <FormError message={errors.countryId.message ?? null} />}
            </div>
            <FormError message={createError} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={isSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Creando...' : 'Crear Region'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

// ── Webhooks tab ───────────────────────────────────────────────────────────────

function WebhooksTab() {
  const { data: endpoints, isLoading, isError } = useWebhookEndpoints()
  const createWebhook = useCreateWebhookEndpoint()
  const deleteWebhook = useDeleteWebhookEndpoint()
  const [showModal, setShowModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateWebhookInput>()

  const onSubmit = async (data: CreateWebhookInput) => {
    setCreateError(null)
    try {
      // events as comma-separated string → array
      const events = typeof data.events === 'string'
        ? (data.events as unknown as string).split(',').map((e) => e.trim()).filter(Boolean)
        : data.events
      await createWebhook.mutateAsync({ ...data, events })
      setShowModal(false)
      reset()
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el webhook')
    }
  }

  const handleDeactivate = async (id: string) => {
    if (!confirm('Desactivar este webhook endpoint?')) return
    try {
      await deleteWebhook.mutateAsync(id)
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al desactivar')
    }
  }

  return (
    <div>
      <SectionHeader title="Webhook Endpoints" onAdd={() => { setShowModal(true); reset(); setCreateError(null) }} />

      {isLoading && <p className="py-8 text-center text-gray-500">Cargando webhooks...</p>}
      {isError && <p className="py-8 text-center text-red-500">Error al cargar los webhooks</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['URL', 'Eventos', 'Estado', 'Descripcion', 'Acciones'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(endpoints ?? []).length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No hay webhook endpoints configurados</td></tr>
              ) : (
                (endpoints ?? []).map((ep) => (
                  <tr key={ep.id}>
                    <td className="max-w-xs truncate px-4 py-3 font-mono text-xs text-blue-600">{ep.url}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{ep.events.join(', ')}</td>
                    <td className="px-4 py-3"><Badge active={ep.active} /></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{ep.description ?? '-'}</td>
                    <td className="px-4 py-3">
                      {ep.active && (
                        <button
                          onClick={() => handleDeactivate(ep.id)}
                          className="text-xs text-red-600 hover:text-red-800 hover:underline"
                        >
                          Desactivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Nuevo Webhook Endpoint" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">URL *</label>
              <input
                type="url"
                {...register('url', { required: 'Requerido' })}
                placeholder="https://ejemplo.com/webhook"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              {errors.url && <FormError message={errors.url.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Eventos * (separados por coma)</label>
              <input
                {...register('events', { required: 'Requerido' })}
                placeholder="account.activated, invoice.paid"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              {errors.events && <FormError message={errors.events.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Secret (opcional — se genera automaticamente si se omite)</label>
              <input
                {...register('secret')}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Descripcion</label>
              <input {...register('description')} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <FormError message={createError} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={isSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Creando...' : 'Crear Webhook'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

// ── API Clients tab ────────────────────────────────────────────────────────────

function ApiClientsTab() {
  const { data: apiClients, isLoading, isError } = useApiClients()
  const createApiClient = useCreateApiClient()
  const toggleApiClient = useToggleApiClient()
  const [showModal, setShowModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [rawKeyResult, setRawKeyResult] = useState<CreateApiClientResult | null>(null)
  const [copied, setCopied] = useState(false)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateApiClientInput>({
    defaultValues: { rateLimit: 100, permissions: [] },
  })

  const onSubmit = async (data: CreateApiClientInput) => {
    setCreateError(null)
    try {
      const result = await createApiClient.mutateAsync({
        ...data,
        rateLimit: Number(data.rateLimit),
        permissions: typeof data.permissions === 'string'
          ? (data.permissions as unknown as string).split(',').map((p) => p.trim()).filter(Boolean)
          : (data.permissions ?? []),
      })
      setShowModal(false)
      reset()
      setRawKeyResult(result)
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : 'Error al crear el API client')
    }
  }

  const handleCopyKey = async () => {
    if (!rawKeyResult?.rawKey) return
    try {
      await navigator.clipboard.writeText(rawKeyResult.rawKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback: select text manually
    }
  }

  const handleToggle = async (id: string, currentActive: boolean) => {
    try {
      await toggleApiClient.mutateAsync({ id, active: !currentActive })
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al cambiar estado')
    }
  }

  return (
    <div>
      <SectionHeader title="API Clients" onAdd={() => { setShowModal(true); reset(); setCreateError(null) }} />

      {isLoading && <p className="py-8 text-center text-gray-500">Cargando API clients...</p>}
      {isError && <p className="py-8 text-center text-red-500">Error al cargar los API clients</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nombre', 'Descripcion', 'Permisos', 'Rate Limit', 'Ultimo uso', 'Estado', 'Acciones'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(apiClients ?? []).length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay API clients configurados</td></tr>
              ) : (
                (apiClients ?? []).map((client) => (
                  <tr key={client.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{client.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{client.description ?? '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {client.permissions.length > 0 ? client.permissions.join(', ') : <span className="text-gray-400">ninguno</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{client.rate_limit} req/min</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(client.last_used_at)}</td>
                    <td className="px-4 py-3"><Badge active={client.active} /></td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggle(client.id, client.active)}
                        className={`text-xs hover:underline ${client.active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                      >
                        {client.active ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <Modal title="Nuevo API Client" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre *</label>
              <input {...register('name', { required: 'Requerido' })} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              {errors.name && <FormError message={errors.name.message ?? null} />}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Descripcion</label>
              <input {...register('description')} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Permisos (separados por coma)</label>
              <input
                {...register('permissions')}
                placeholder="billing:read, provisioning:read"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Rate limit (req/min)</label>
              <input
                type="number"
                min={1}
                {...register('rateLimit', { valueAsNumber: true, min: { value: 1, message: 'Debe ser >= 1' } })}
                className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <FormError message={createError} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={isSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                {isSubmitting ? 'Creando...' : 'Crear API Client'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Raw key one-time display modal */}
      {rawKeyResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">API Client creado</h3>
            </div>
            <div className="p-6">
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-medium text-amber-800">
                  Esta clave solo se muestra una vez. Copiala ahora — no podras recuperarla.
                </p>
              </div>
              <p className="mb-2 text-sm font-medium text-gray-700">
                Cliente: <span className="font-semibold">{rawKeyResult.apiClient.name}</span>
              </p>
              <div className="mb-4 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                <code className="flex-1 break-all text-xs font-mono text-gray-800">{rawKeyResult.rawKey}</code>
                <button
                  onClick={handleCopyKey}
                  className="shrink-0 rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300"
                >
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => { setRawKeyResult(null); setCopied(false) }}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Entendido, ya la copie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main ConfigPage ────────────────────────────────────────────────────────────

export function ConfigPage() {
  const [activeTab, setActiveTab] = useState<Tab>('planes')

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Configuracion del Sistema</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gestiona planes, add-ons, geografias, webhooks y credenciales de API.
        </p>
      </div>

      {/* Tab bar */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-2 overflow-x-auto">
          {TABS.map(({ id, label }) => (
            <TabButton key={id} active={activeTab === id} onClick={() => setActiveTab(id)}>
              {label}
            </TabButton>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'planes' && <PlanesTab />}
      {activeTab === 'addons' && <AddonsTab />}
      {activeTab === 'paises' && <PaisesTab />}
      {activeTab === 'regiones' && <RegionesTab />}
      {activeTab === 'webhooks' && <WebhooksTab />}
      {activeTab === 'api-clients' && <ApiClientsTab />}
    </div>
  )
}
